import { Link } from 'react-router';
import { Scale, ArrowLeft, FileText } from 'lucide-react';
import { motion } from 'motion/react';
import { SEO, SEO_CONFIGS } from '../components/SEO';

export function TermsOfService() {
  return (
    <>
      <SEO {...SEO_CONFIGS.terms} />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Header */}
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-6 py-6">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
            
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                <Scale className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Terms of Service</h1>
                <p className="text-slate-600 mt-1">Last updated: March 9, 2026</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto px-6 py-12"
        >
          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8 md:p-12 space-y-8 prose prose-slate max-w-none">
            
            {/* Introduction */}
            <section>
              <p className="text-lg text-slate-700 leading-relaxed">
                These Terms of Service ("Terms") govern your access to and use of HelioROI, a solar energy investment analysis platform ("Service") operated by <strong>CRRSoft, Inc.</strong> ("Company," "we," "us," or "our"), a corporation organized under the laws of Delaware, United States.
              </p>
              <p className="text-slate-700 leading-relaxed mt-4">
                By accessing or using the Service, you ("User," "you," or "your") agree to be bound by these Terms. If you do not agree to these Terms, you must not access or use the Service.
              </p>
            </section>

            {/* 1. Acceptance of Terms */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <FileText className="w-6 h-6 text-blue-600" />
                1. Acceptance of Terms
              </h2>
              <p className="text-slate-700 leading-relaxed">
                By creating an account, accessing, or using HelioROI, you represent and warrant that:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>You are at least 18 years of age or the age of majority in your jurisdiction;</li>
                <li>You have the legal capacity to enter into binding contracts;</li>
                <li>All information you provide is accurate, current, and complete;</li>
                <li>You will comply with all applicable laws and regulations.</li>
              </ul>
            </section>

            {/* 2. Service Description */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">2. Service Description</h2>
              <p className="text-slate-700 leading-relaxed">
                HelioROI is a Software-as-a-Service (SaaS) platform that provides solar energy project analysis, financial modeling, and investment insights. The Service includes but is not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Solar irradiation data analysis using third-party APIs (NASA POWER, Open-Meteo);</li>
                <li>Energy production forecasting and system design calculations;</li>
                <li>Financial modeling including ROI, NPV, IRR, and payback period analysis;</li>
                <li>Risk assessment and sensitivity analysis;</li>
                <li>Comparative analysis tools for multiple project scenarios.</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                <strong className="text-red-600">DISCLAIMER:</strong> HelioROI provides informational analysis tools only. We do not provide investment advice, engineering services, or professional consulting. All analyses are based on mathematical models and third-party data sources and should not be construed as guarantees of actual project performance.
              </p>
            </section>

            {/* 3. User Accounts and Registration */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">3. User Accounts and Registration</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mt-4">3.1 Account Creation</h3>
              <p className="text-slate-700 leading-relaxed">
                To access certain features, you must create an account by providing a valid email address and password. You are responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Maintaining the confidentiality of your account credentials;</li>
                <li>All activities that occur under your account;</li>
                <li>Immediately notifying us of any unauthorized access or security breach.</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">3.2 Email Verification</h3>
              <p className="text-slate-700 leading-relaxed">
                You must verify your email address to activate your account. Failure to verify within the specified timeframe may result in account suspension or deletion.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">3.3 Account Termination</h3>
              <p className="text-slate-700 leading-relaxed">
                We reserve the right to suspend or terminate your account at any time, with or without notice, for violations of these Terms or for any other reason at our sole discretion.
              </p>
            </section>

            {/* 4. Credit System and Payment Terms */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">4. Credit System and Payment Terms</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mt-4">4.1 Pay-to-Use Model</h3>
              <p className="text-slate-700 leading-relaxed">
                HelioROI operates on a pay-to-use credit system. Each analysis consumes one (1) credit. Available packages:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>Starter:</strong> 5 credits for $29.00 USD</li>
                <li><strong>Professional:</strong> 20 credits for $79.00 USD</li>
                <li><strong>Business:</strong> 100 credits for $249.00 USD</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">4.2 Payment Processing</h3>
              <p className="text-slate-700 leading-relaxed">
                All payments are processed through Stripe, a third-party payment processor. By making a purchase, you agree to Stripe's Terms of Service. We do not store your credit card information.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">4.3 No Refunds</h3>
              <p className="text-slate-700 leading-relaxed">
                <strong>All credit purchases are final and non-refundable.</strong> Credits do not expire but are forfeited upon account termination.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">4.4 Pricing Changes</h3>
              <p className="text-slate-700 leading-relaxed">
                We reserve the right to modify pricing at any time. Price changes will not affect credits already purchased.
              </p>
            </section>

            {/* 5. Intellectual Property */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">5. Intellectual Property Rights</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mt-4">5.1 Ownership</h3>
              <p className="text-slate-700 leading-relaxed">
                The Service, including all software, algorithms, designs, trademarks, and content (excluding User Content), is the exclusive property of CRRSoft, Inc. and is protected by U.S. and international copyright, trademark, and intellectual property laws.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">5.2 Limited License</h3>
              <p className="text-slate-700 leading-relaxed">
                Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for your internal business purposes only.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">5.3 Restrictions</h3>
              <p className="text-slate-700 leading-relaxed">
                You agree not to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Reverse engineer, decompile, or disassemble the Service;</li>
                <li>Copy, modify, or create derivative works based on the Service;</li>
                <li>Use automated systems (bots, scrapers) to access the Service;</li>
                <li>Resell, sublicense, or otherwise commercialize the Service;</li>
                <li>Remove or alter any proprietary notices or labels.</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">5.4 User Content</h3>
              <p className="text-slate-700 leading-relaxed">
                You retain ownership of all data you input into the Service ("User Content"). By using the Service, you grant us a worldwide, royalty-free license to use, store, and process User Content solely to provide the Service.
              </p>
            </section>

            {/* 6. Disclaimers and Limitations */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">6. Disclaimers and Limitations of Liability</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mt-4">6.1 "As-Is" Basis</h3>
              <p className="text-slate-700 leading-relaxed">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR ACCURACY.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">6.2 No Investment Advice</h3>
              <p className="text-slate-700 leading-relaxed">
                <strong>HelioROI does not provide investment, financial, legal, or engineering advice.</strong> All analyses are informational tools only. You should consult with qualified professionals before making investment decisions.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">6.3 Third-Party Data</h3>
              <p className="text-slate-700 leading-relaxed">
                We rely on third-party APIs (NASA POWER, Open-Meteo) for weather and irradiation data. We do not guarantee the accuracy, completeness, or reliability of such data.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">6.4 Limitation of Liability</h3>
              <p className="text-slate-700 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, CRRSOFT, INC. SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR BUSINESS OPPORTUNITIES ARISING FROM:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Your use or inability to use the Service;</li>
                <li>Reliance on any analysis or data provided by the Service;</li>
                <li>Errors, inaccuracies, or omissions in the Service;</li>
                <li>Unauthorized access to your account or data.</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                <strong>Our total liability shall not exceed the amount you paid to us in the twelve (12) months preceding the claim.</strong>
              </p>
            </section>

            {/* 7. Indemnification */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">7. Indemnification</h2>
              <p className="text-slate-700 leading-relaxed">
                You agree to indemnify, defend, and hold harmless CRRSoft, Inc., its officers, directors, employees, and agents from any claims, liabilities, damages, losses, or expenses (including reasonable attorneys' fees) arising from:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Your violation of these Terms;</li>
                <li>Your use or misuse of the Service;</li>
                <li>Your violation of any third-party rights;</li>
                <li>Investment decisions made based on Service analyses.</li>
              </ul>
            </section>

            {/* 8. Governing Law */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">8. Governing Law and Dispute Resolution</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mt-4">8.1 Governing Law</h3>
              <p className="text-slate-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law principles.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">8.2 Jurisdiction</h3>
              <p className="text-slate-700 leading-relaxed">
                Any disputes arising from these Terms or the Service shall be resolved exclusively in the state or federal courts located in Delaware, and you consent to the personal jurisdiction of such courts.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">8.3 Arbitration</h3>
              <p className="text-slate-700 leading-relaxed">
                At our sole discretion, we may require binding arbitration under the rules of the American Arbitration Association for disputes exceeding $10,000 USD.
              </p>
            </section>

            {/* 9. Modifications */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">9. Modifications to Terms</h2>
              <p className="text-slate-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. Material changes will be notified via email or through the Service. Continued use of the Service after modifications constitutes acceptance of the updated Terms.
              </p>
            </section>

            {/* 10. Miscellaneous */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900">10. Miscellaneous</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mt-4">10.1 Entire Agreement</h3>
              <p className="text-slate-700 leading-relaxed">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and CRRSoft, Inc. regarding the Service.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">10.2 Severability</h3>
              <p className="text-slate-700 leading-relaxed">
                If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">10.3 Waiver</h3>
              <p className="text-slate-700 leading-relaxed">
                Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-4">10.4 Assignment</h3>
              <p className="text-slate-700 leading-relaxed">
                You may not assign or transfer these Terms without our prior written consent. We may assign these Terms without restriction.
              </p>
            </section>

            {/* Contact */}
            <section className="border-t border-slate-200 pt-8">
              <h2 className="text-2xl font-bold text-slate-900">11. Contact Information</h2>
              <p className="text-slate-700 leading-relaxed">
                For questions about these Terms, please contact us at:
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mt-4">
                <p className="text-slate-900 font-semibold">CRRSoft, Inc.</p>
                <p className="text-slate-700">7901 4TH ST N STE 300</p>
                <p className="text-slate-700">ST. PETERSBURG, FL 33702</p>
                <p className="text-slate-700">United States</p>
                <p className="text-slate-700 mt-2">Phone: <a href="tel:+17273902400" className="text-blue-600 hover:underline">+1 (727) 390-2400</a></p>
                <p className="text-slate-700">Email: <a href="mailto:info@helioroi.com" className="text-blue-600 hover:underline">info@helioroi.com</a></p>
                <p className="text-slate-700 mt-2">Service: HelioROI</p>
                <p className="text-slate-700">Website: <a href="https://helioroi.com" className="text-blue-600 hover:underline">https://helioroi.com</a></p>
              </div>
            </section>

            {/* Footer Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
              <p className="text-sm text-blue-900">
                <strong>Important Notice:</strong> By clicking "I Agree" during account registration, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}